/**
 * Column selection for a grid export: which columns land in the file, in
 * which order.
 *
 * The backend descriptor is the authority on both what is exportable and the
 * file's column order — columns land in descriptor order, so a payload field
 * declared beside the column whose cell folds it in (a name-cell description
 * line) exports beside it, enforced server-side. The screen decides only the
 * membership of toggleable columns: a column the user has hidden stays out of
 * the file, one marked `export: "always"` is in every file, and ones the
 * descriptor marks non-exportable (an image URL, an actions cluster) never
 * appear. The grid's own generated chrome (the selection checkbox) belongs to
 * no descriptor column and so falls out on its own.
 *
 * A grid whose rows carry a value computed on the client — one derived from a
 * nested object the descriptor exposes only as a whole — appends it through
 * `extraColumns`. That is the exception, not the seam to build on: a value
 * worth exporting is usually worth declaring, and a descriptor column gets
 * inclusion, order, and typing at once.
 *
 * Contains:
 * - buildExportColumns — descriptor columns + visible fields → CSV column defs
 *
 * Returns plain JS — no JSX.
 */

import { normalizeDescriptorColumn } from '@sophocles/data-grids/grid/normalizeDescriptorColumn';
import { resolveDescriptorLabel } from '@sophocles/data-grids/grid/resolveDescriptorLabel';

import { descriptorExportValue } from './exportValues';

/** Columns whose on-screen value is interaction, never data. */
const NON_DATA_TYPES = new Set(['actions']);

/**
 * Build the `rowsToCsv` column descriptors for a grid export.
 *
 * @param {object[]} specColumns - The descriptor's raw `columns` array.
 * @param {object} [opts]
 * @param {string[]} [opts.visibleFields] - Fields currently on screen. Decides
 *   membership only — order comes from the descriptor. An empty or absent list
 *   falls back to the descriptor's own default-visible set — the mobile card
 *   view collapses to a single generated column, so there is no on-screen
 *   column set to read there.
 * @param {object} [opts.overrides] - `{ [field]: { headerName?, valueGetter? } }`
 *   per-field export overrides, merged last. For a value whose meaning inverts
 *   its raw form (a `deactivated` flag shown as Active/Deactivated).
 * @param {Array<{ field: string, headerName: string, valueGetter: (row: object) => string }>}
 *   [opts.extraColumns] - Client-computed columns the descriptor does not own,
 *   appended after the descriptor ones. They answer to no visibility toggle, so
 *   they are always written.
 * @param {Function} [opts.translate] - i18next translator for column headers.
 * @param {Object<string, string>} [opts.terms] - Tenant vocabulary, keyed by
 *   term field name, interpolated into headers and enum labels the same way
 *   the on-screen columns do it.
 * @param {object} [opts.formatters] - config.formatters, threaded to the values.
 * @param {Function} [opts.resolveReference] - config.resolveReference, threaded
 *   to the values.
 * @returns {Array<{ field: string, headerName: string, valueGetter: (row: object) => string }>}
 */
export function buildExportColumns(
  specColumns,
  { visibleFields, overrides = {}, extraColumns = [], translate, terms, formatters, resolveReference } = {},
) {
  const normalized = (specColumns ?? []).map(specCol => normalizeDescriptorColumn(specCol));

  const visible = visibleFields?.length
    ? new Set(visibleFields)
    : new Set(normalized.filter(column => !column.payloadOnly && !column.defaultHidden).map(column => column.field));

  const descriptorColumns = normalized
    .filter(column => column.exportable && !NON_DATA_TYPES.has(column.type))
    // Membership: an export:"always" column is in every file; a screen column
    // is in the file while it is on screen. Order is the descriptor's.
    .filter(column => column.exportAlways || (!column.payloadOnly && visible.has(column.field)))
    .map(column => ({
      field: column.field,
      headerName: resolveDescriptorLabel(translate, column.headerLabel, column.headerFallback, terms),
      valueGetter: row =>
        descriptorExportValue(row[column.field], column, { formatters, translate, terms, resolveReference }),
      ...overrides[column.field],
    }));

  return [...descriptorColumns, ...extraColumns];
}
